import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  Chip,
  Alert,
  Snackbar,
  Divider,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as SellIcon,
  RemoveCircle as RemoveIcon,
} from '@mui/icons-material';
import { recipesApi } from '../api/recipes.api';
import { productsApi } from '../api/products.api';
import { stationsApi } from '../api/stations.api';
import { Recipe, RecipeIngredient, Product, Station } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PaginatedTable from '../components/common/PaginatedTable';

const Recipes: React.FC = () => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [sellId, setSellId] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [salePrice, setSalePrice] = useState(0);
  const [category, setCategory] = useState('');
  const [stationId, setStationId] = useState<number | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('todas');

  // Temp ingredient state
  const [tempProductId, setTempProductId] = useState<number | null>(null);
  const [tempQuantity, setTempQuantity] = useState<number>(0);

  const loadData = async () => {
    try {
      const [recRes, prodRes, stRes] = await Promise.all([
        recipesApi.getAll(),
        productsApi.getAll(),
        // Cargar estaciones para el dropdown; si falla, el resto sigue funcionando
        stationsApi.getAll().catch(() => ({ data: [] as Station[] })),
      ]);
      setRecipes(recRes.data);
      setProducts(prodRes.data);
      setStations(stRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(recipes.map((r) => r.category).filter(Boolean))) as string[];
    return cats.sort();
  }, [recipes]);

  const filteredRecipes = useMemo(() => {
    if (categoryFilter === 'todas') return recipes;
    if (categoryFilter === 'sin-categoria') return recipes.filter((r) => !r.category);
    return recipes.filter((r) => r.category === categoryFilter);
  }, [recipes, categoryFilter]);

  const cocinaStations = stations.filter((s) => s.type === 'cocina');
  const barStations = stations.filter((s) => s.type === 'bar');

  const resetForm = () => {
    setName('');
    setDescription('');
    setSalePrice(0);
    setCategory('');
    setStationId(null);
    setIngredients([]);
    setEditId(null);
    setTempProductId(null);
    setTempQuantity(0);
  };

  const handleOpenCreate = () => {
    resetForm();
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (recipe: Recipe) => {
    setName(recipe.name);
    setDescription(recipe.description || '');
    setSalePrice(Number(recipe.salePrice) || 0);
    setCategory(recipe.category || '');
    setStationId(recipe.stationId ?? null);
    setIngredients(
      (recipe.ingredients || []).map((ing) => ({
        ...ing,
        productId: Number(ing.productId),
        quantity: Number(ing.quantity),
      })),
    );
    setEditId(recipe.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleAddIngredient = () => {
    if (!tempProductId || tempQuantity <= 0) return;
    const exists = ingredients.find((i) => i.productId === tempProductId);
    if (exists) return;
    setIngredients([...ingredients, { productId: tempProductId, quantity: tempQuantity }]);
    setTempProductId(null);
    setTempQuantity(0);
  };

  const handleRemoveIngredient = (productId: number) => {
    setIngredients(ingredients.filter((i) => i.productId !== productId));
  };

  const calculateCost = () => {
    return ingredients.reduce((total, ing) => {
      const product = products.find((p) => p.id === ing.productId);
      return total + Number(product?.referencePrice || 0) * Number(ing.quantity);
    }, 0);
  };

  const handleSave = async () => {
    try {
      const data: any = {
        name,
        description,
        salePrice: Number(salePrice) || 0,
        stationId: stationId ?? null,
        ingredients: ingredients.map((ing) => ({
          productId: Number(ing.productId),
          quantity: Number(ing.quantity),
        })),
      };
      if (category) data.category = category;
      if (isEditing && editId) {
        await recipesApi.update(editId, data);
      } else {
        await recipesApi.create(data);
      }
      setModalOpen(false);
      resetForm();
      setSnackbar({
        open: true,
        message: isEditing ? 'Receta actualizada' : 'Receta creada',
        severity: 'success',
      });
      loadData();
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setSnackbar({
        open: true,
        message: Array.isArray(msg) ? msg.join(', ') : msg || 'Error al guardar la receta',
        severity: 'error',
      });
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await recipesApi.delete(deleteId);
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSell = async () => {
    try {
      await recipesApi.sell(sellId);
      setSellDialogOpen(false);
      setSnackbar({ open: true, message: 'Venta registrada, ingredientes descontados', severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al registrar venta',
        severity: 'error',
      });
      setSellDialogOpen(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const tableColumns = [
    { id: 'name', label: 'Nombre', minWidth: 180 },
    {
      id: 'category',
      label: 'Categoria',
      minWidth: 120,
      format: (val: string) => val ? <Chip label={val} size="small" color="primary" variant="outlined" /> : <Chip label="Sin categoria" size="small" variant="outlined" color="default" />,
    },
    {
      id: 'station',
      label: 'Estacion',
      minWidth: 130,
      format: (_: any, row: any) => row.station ? (
        <Chip
          label={`${row.station.type === 'bar' ? '🍺' : '🍳'} ${row.station.name}`}
          size="small"
          color={row.station.type === 'bar' ? 'info' : 'warning'}
        />
      ) : (
        <Chip label="Sin asignar" size="small" variant="outlined" />
      ),
    },
    { id: 'description', label: 'Descripcion', minWidth: 150, format: (val: string) => val || '-' },
    {
      id: 'salePrice',
      label: 'Precio Venta',
      align: 'right' as const,
      minWidth: 100,
      format: (val: number) => `S/ ${Number(val || 0).toFixed(2)}`,
    },
    {
      id: 'ingredients',
      label: 'Ingredientes',
      align: 'center' as const,
      minWidth: 90,
      format: (_: any, row: any) => <Chip label={row.ingredients?.length || 0} size="small" />,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Recetas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nueva Receta
        </Button>
      </Box>

      {/* Filtro de categorias */}
      {categories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          <Chip
            label="Todas"
            color={categoryFilter === 'todas' ? 'primary' : 'default'}
            onClick={() => setCategoryFilter('todas')}
          />
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              color={categoryFilter === cat ? 'primary' : 'default'}
              onClick={() => setCategoryFilter(cat)}
            />
          ))}
        </Box>
      )}

      <PaginatedTable
        columns={tableColumns}
        rows={filteredRecipes}
        defaultRowsPerPage={25}
        searchFields={['name', 'description', 'category']}
        exportFileName="recetas"
        emptyMessage="No hay recetas registradas"
        renderActions={(row: Recipe) => (
          <>
            <IconButton
              size="small"
              color="success"
              title="Vender"
              onClick={() => { setSellId(row.id); setSellDialogOpen(true); }}
            >
              <SellIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(row.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Receta' : 'Nueva Receta'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Nombre" value={name}
                onChange={(e) => setName(e.target.value)} required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Precio de Venta" type="number" value={salePrice || ''}
                onChange={(e) => setSalePrice(Number(e.target.value))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={categories}
                value={category}
                onChange={(_, val) => setCategory(val || '')}
                onInputChange={(_, val) => setCategory(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Categoria" placeholder="Ej: Entrada, Pollo, Postre..." />
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                select
                label="Estacion de preparacion"
                value={stationId ?? ''}
                onChange={(e) => setStationId(e.target.value ? Number(e.target.value) : null)}
                helperText="Estacion a la que se envia este plato o bebida al tomar el pedido."
              >
                <MenuItem value="">
                  <em>— Sin asignar —</em>
                </MenuItem>
                {cocinaStations.length > 0 && (
                  <ListSubheader sx={{ lineHeight: '32px' }}>COCINA</ListSubheader>
                )}
                {cocinaStations.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isActive === false ? ' (inactiva)' : ''}
                  </MenuItem>
                ))}
                {barStations.length > 0 && (
                  <ListSubheader sx={{ lineHeight: '32px' }}>BAR</ListSubheader>
                )}
                {barStations.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isActive === false ? ' (inactiva)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {stations.length === 0 && (
              <Grid size={12}>
                <Alert severity="warning">
                  No hay estaciones configuradas. Crea una en "Estaciones (Bar/Cocina)"
                  para asignar los platos y bebidas.
                </Alert>
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth label="Descripcion" value={description}
                onChange={(e) => setDescription(e.target.value)} multiline rows={2}
              />
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Ingredientes
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 5 }}>
              <Autocomplete
                options={products.filter((p) => !ingredients.find((i) => i.productId === p.id))}
                getOptionLabel={(p) => `${p.name} (${p.unit})`}
                value={products.find((p) => p.id === tempProductId) || null}
                onChange={(_, val) => setTempProductId(val?.id || null)}
                renderInput={(params) => <TextField {...params} label="Producto" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth label="Cantidad" type="number" size="small"
                value={tempQuantity || ''}
                onChange={(e) => setTempQuantity(Number(e.target.value))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button variant="outlined" onClick={handleAddIngredient} startIcon={<AddIcon />}>
                Agregar Ingrediente
              </Button>
            </Grid>

            {ingredients.length > 0 && (
              <Grid size={12}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Precio Ref.</TableCell>
                      <TableCell align="right">Costo</TableCell>
                      <TableCell align="center">Quitar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ingredients.map((ing) => {
                      const prod = products.find((p) => p.id === ing.productId);
                      const cost = Number(prod?.referencePrice || 0) * Number(ing.quantity);
                      return (
                        <TableRow key={ing.productId}>
                          <TableCell>{prod?.name || '-'}</TableCell>
                          <TableCell align="right">{ing.quantity} {prod?.unit || ''}</TableCell>
                          <TableCell align="right">S/ {Number(prod?.referencePrice || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">S/ {cost.toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleRemoveIngredient(ing.productId)}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>Costo Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        S/ {calculateCost().toFixed(2)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 700, color: '#10B981' }}>Margen</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#10B981' }}>
                        S/ {(salePrice - calculateCost()).toFixed(2)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Receta"
        message="¿Estas seguro de que deseas eliminar esta receta?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <ConfirmDialog
        open={sellDialogOpen}
        title="Vender Receta"
        message="Se descontaran los ingredientes del inventario. ¿Continuar?"
        onConfirm={handleSell}
        onCancel={() => setSellDialogOpen(false)}
        confirmText="Vender"
        confirmColor="secondary"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Recipes;
